<template>
  <floating-element
    :visible="loginPopupVisible"
    transition-name="loginformanimation"
    class="loginguard-popup"
  >
    <div class="loginguard-popup__wrapper">
      <button
        aria-label="Close"
        type="button"
        class="loginguard-popup__closebutton button-icon"
        @click="abortLogin"
      >
        <lucide-icon name="x" />
      </button>

      <template v-if="activeFormId === 'login'">
        <login-form
          v-model:email="email"
          class="loginguard-popup__form"
          @login="onLogin"
        >
          <template #header>
            <simcision-logo class="loginguard-popup__logo" />
          </template>
          <template #forgot-password>
            <button
              type="button"
              class="loginguard-popup__link"
              @click="activeFormId = 'reset-password'"
            >
              {{ $t("labels.forgotPassword") }}
            </button>
          </template>
        </login-form>

        <div class="loginguard-popup__footer">
          <button
            type="button"
            class="loginguard-popup__link"
            @click="activeFormId = 'register'"
          >
            {{ $t("labels.register") }}
          </button>
        </div>
      </template>

      <template v-else-if="activeFormId === 'register'">
        <register-form v-model:email="email" @register="onLogin()">
          <template #after="{ step }">
            <div v-if="step == 'register'" class="loginguard-popup__footer mt-5 pt-6" style="border-top: 1px solid var(--color-border-light)">
              <button
                type="button"
                class="loginguard-popup__link"
                @click="activeFormId = 'login'"
              >
                {{ $t("labels.login") }}
              </button>
            </div>
          </template>
        </register-form>

      </template>

      <template v-else-if="activeFormId === 'reset-password'">
        <reset-password-form v-model:email="email"></reset-password-form>

        <div class="loginguard-popup__footer">
          <button
            type="button"
            class="loginguard-popup__link"
            @click="activeFormId = 'register'"
          >
            {{ $t("labels.register") }}
          </button>
          <button
            type="button"
            class="loginguard-popup__link"
            @click="activeFormId = 'login'"
          >
            {{ $t("labels.login") }}
          </button>
        </div>
      </template>
    </div>
  </floating-element>
</template>

<script setup lang="ts">
/**
 * This is a login popup that is beeing shown when the user
 * needs to log in again.
 * Triggered by eventHub.
 */

import { onBeforeUnmount, ref } from "vue";
import eventHub from "@/helpers/utility/Events";

import LucideIcon from "@core/ui/LucideIcon.vue";
import SimcisionLogo from "@core/ui/SimcisionLogo.vue";

import LoginForm from "@manager/auth/LoginForm.vue";
import RegisterForm from "@manager/auth/RegisterForm.vue";
import ResetPasswordForm from "@manager/auth/ResetPasswordForm.vue";
import FloatingElement from "@core/ui/FloatingElement.vue";

import { useUser } from "@/api/user.store";

const { user } = useUser();

const email = ref(user.value?.email || "");
const loginPopupVisible = ref(false);
const activeFormId = ref<"login" | "register" | "reset-password">("login");

function showPopup() {
  loginPopupVisible.value = true;
}
function hidePopup() {
  loginPopupVisible.value = false;
}
function onLogin() {
  hidePopup();
}
function abortLogin() {
  hidePopup();
  eventHub.emit("login-aborted");
}

// Connect to event hub.
eventHub.on("request-login", showPopup);
onBeforeUnmount(() => {
  eventHub.off("request-login", showPopup);
});
</script>

<style lang="scss">
.loginguard-popup {
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-backdrop);

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: auto;
    width: 100%;
    max-width: 30rem;

    padding: 3rem;
    background-color: var(--color-background-deep);
    box-shadow: 0 5px 20px var(--color-shadow-1);
    position: relative;
    border-radius: 1rem;
    max-height: 100vh;
    overflow: auto;
  }

  &__logo {
    margin: 0 auto;
  }

  &__closebutton {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }

  &__footer {
    display: flex;
    justify-content: center;
    gap: 1rem;
  }

  &__link {
    cursor: pointer;
    color: var(--color-action);
    border: none;
    background: none;
    display: inline;
    padding: 0 3px;
    font-weight: 600;
  }
}

.loginformanimation {
  &-enter-active {
    transition: background-color 1s;

    .loginguard-popup__wrapper {
      transition: transform 300ms ease-out, opacity 300ms ease-out;
    }
  }
  &-leave-active {
    transition: background-color 1s;

    .loginguard-popup__wrapper {
      transition: transform 150ms ease-in, opacity 150ms ease-in;
    }
  }
  &-enter-from,
  &-leave-to {
    background-color: rgba(255, 255, 255, 0);

    .loginguard-popup__wrapper {
      opacity: 0;
      transform: translate3d(0, 100%, 0);
    }
  }
}
</style>
